<template>
  <loading v-if="isLoading"></loading>
  <p v-else-if="!bukaDetail" class="aligncenter">表示できる内容がありません</p>
  <div class="container buka-edit-form" v-else>
    <!-- {{ bukaDetail }} -->
    <div class="form">
      <div class="item syozoku" v-if="authStore.isAdmin">
        <b>所属</b>
        <select-box :options="syozokuOptions" :hasBlank="false" v-model="bukaDetail.mst_syozoku_id"
          :error="getError(bukaDetail, 'mst_syozoku_id')"></select-box>
      </div>

      <div class="item code">
        <b>部課コード</b>
        <input-text type="text" maxlength="3" v-model="bukaDetail.buka_code" class="input-code"
          :error="getError(bukaDetail, 'buka_code')"></input-text>
      </div>

      <div class="item name">
        <b class="required">部課名</b>
        <input-text type="text" maxlength="100" v-model="bukaDetail.buka_name"
          :error="getError(bukaDetail, 'buka_name')"></input-text>
        <p>※雇用契約や打刻時の表示を変更する場合は部課名ではなく就業場所名を変更してください</p>
      </div>

      <div class="item kana">
        <b class="required">部課名（カナ）</b>
        <input-text type="text" maxlength="100" v-model="bukaDetail.buka_kana"
          :error="getError(bukaDetail, 'buka_kana')"></input-text>
      </div>

      <div class="buttons">
        <button type="button" class="btn success" @click="saveBuka()" :disabled="isSaving">
          <template v-if="isSaving">
            <i class="fa-solid fa-spinner fa-spin-pulse"></i>処理中…
          </template>
          <template v-else>
            <i class="fa-solid fa-floppy-disk"></i>保存
          </template>
        </button>
      </div>
      <p v-if="!isEditMode" class="aligncenter">※部課に属する就業場所の設定は保存後に行えます</p>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, Ref, ref } from 'vue'
import { useBukaDetail } from '@/composable/master/buka/buka';
import { useSyozoku } from '@/composable/master/syozoku/syozoku';
import { useAuthStore } from '@/store/auth';
import { DialogService } from '@/service/DialogService';
import { useForm } from '@/composable/form/form';
import { RouterConst } from '@/const/Router';
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';
import { Logger } from '@/utils/Logger';

export default defineComponent({
  setup() {
    const { getBukaDetail, initBukaDetail, bukaDetail, upsertBukaDetail } = useBukaDetail()
    const { getAllSyozokuOptions, syozokuOptions } = useSyozoku()
    const authStore = useAuthStore()
    const router = useRouter()

    const isLoading = ref(false)
    const isSaving = ref(false)
    const isEditMode = ref(false) // 編集モードフラグ（false=新規作成モード）
    const { getError } = useForm()

    const route = useRoute()
    const bukaId: Ref<number | null> = ref(null)

    // 同一コンポーネント内でのルーティング発生時
    onBeforeRouteUpdate(async () => {
      // 基本的に新規作成時にsaveBukaが成功するとここにくる
      // 自コンポーネントへの遷移時は、push前のコンポーネントが再利用されonMountedが走らないのでここで読み込み処理を行う
      isLoading.value = true // 一応
      await load()
      isLoading.value = false
    });

    onMounted(async () => {
      bukaId.value = parseInt(route.params.bukaId as string, 10)
      await load()
    })

    const load = async () => {
      Logger.debug('読み込み処理の実行', { '部課ID': bukaId.value, 'URIに含まれる部課ID': parseInt(route.params.id as string, 10), '編集モード': isEditMode.value })
      isLoading.value = true
      if (authStore.isAdmin) {
        await getAllSyozokuOptions()
      }

      if (bukaId.value) {
        isEditMode.value = true
      }

      if (isEditMode.value && bukaId.value) {
        await getBukaDetail(bukaId.value)
      } else {
        await initBukaDetail()
      }
      isLoading.value = false
    }

    const saveBuka = async () => {
      const dialogResult = await DialogService.question({
        html: '保存しますか？<br>既存の部課を変更した場合、雇用契約等の表示にも影響がある点にご注意ください。',
        showCancelButton: true,
        confirmButtonText: '保存する',
        cancelButtonText: 'キャンセル',
      })

      if (!dialogResult.isConfirmed) {
        return
      }

      isSaving.value = true
      try {
        const res = await upsertBukaDetail(bukaDetail)

        if (isEditMode.value && res.result) {
          // 編集モードでの成功時
          load()
        } else if (!isEditMode.value && res.result) {
          // 新規作成モードでの成功時
          isLoading.value = true // falseにする処理はonBeforeRouteUpdateで行っている（遷移中もローディング表示にさせるため）
          bukaId.value = res.bukaId

          Logger.debug('部課の新規作成 -> 成功', { '部課ID': bukaId.value })

          // 新規作成が成功した場合、編集モードへ移行するためIDを持って自コンポーネントをpushする
          // replaceで遷移することで新規画面の履歴を残さないようにしている
          router.replace({ name: RouterConst.MST_BUKA_EDIT, params: { bukaId: bukaId.value } })
          // ここでrouter.pushするとonBeforeRouteUpdateに制御が移る
        } else {
          // モード問わず失敗時
          Logger.debug('部課の新規作成または更新 -> 失敗', { '部課ID': res.bukaId, 'URIに含まれる部課ID': parseInt(route.params.bukaId as string, 10), '編集モード': isEditMode.value })
        }
      } catch {
      } finally {
        isSaving.value = false
        Logger.debug('finally')
      }
    }

    return {
      load,
      bukaDetail,
      syozokuOptions,
      saveBuka,
      isLoading,
      isSaving,
      authStore,
      isEditMode,
      getError
    }
  },
})
</script>

<style lang="scss" scoped>
@import "@scss/components/buka-edit.scss";
</style>
